import { useEffect, useState } from 'react';

/**
 * Shared AI-generation job lifecycle (extracted verbatim from BannerEditor's
 * generate mode, 2026-09-09) — start a BullMQ job via the caller's producer,
 * poll `GET /jobs/:jobId?queue=…` every 1.5s, surface variants/stage/errors.
 * Used by BannerEditor and LogoEditor; the polling contract lives in
 * CLAUDE.md (Async Jobs).
 */

export type GenerationJobState = 'idle' | 'waiting' | 'active' | 'completed' | 'failed';
export type GenerationVariant = { url: string; width: number; height: number };

interface UseGenerationJobOpts {
  /** Producer call — caller closes over orgId/token. Must resolve { jobId }. */
  startJob: (input: any) => Promise<{ jobId: string }>;
  /** Status call — `GET /jobs/:jobId?queue=<name>` shaped. */
  getJobStatus: (jobId: string, token: string) => Promise<any>;
  /** clerkShim's getToken (nullable when signed out — requests then 401). */
  getToken: () => Promise<string | null>;
  /** Shown when a job sits `waiting` >30s (worker died mid-flight — the
   * producer's liveness probe catches the never-started case). Parameterized
   * so each asset keeps its own wording. */
  offlineMessage: string;
}

export default function useGenerationJob({ startJob, getJobStatus, getToken, offlineMessage }: UseGenerationJobOpts) {
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobState, setJobState] = useState<GenerationJobState>('idle');
  const [stage, setStage] = useState<string | undefined>(undefined);
  const [variants, setVariants] = useState<GenerationVariant[]>([]);
  const [error, setError] = useState<string | null>(null);

  const start = async (input: any) => {
    setError(null);
    setVariants([]);
    // Clear the prior jobId so the polling effect doesn't briefly poll the
    // already-completed previous job (which would race and flip jobState
    // back to 'completed', orphaning the new job).
    setJobId(null);
    setJobState('waiting');
    setStage(undefined);
    try {
      const { jobId: newJobId } = await startJob(input);
      setJobId(newJobId);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to start generation');
      setJobState('failed');
    }
  };

  /** Caller-side validation errors (org not loaded, bad hex …). Optionally
   * force a state so pre-flight failures don't leave a stale spinner. */
  const fail = (message: string, nextState?: GenerationJobState) => {
    setError(message);
    if (nextState) setJobState(nextState);
  };

  const reset = () => {
    setJobId(null);
    setJobState('idle');
    setStage(undefined);
    setVariants([]);
    setError(null);
  };

  // Poll job status while a generation is in flight.
  useEffect(() => {
    if (!jobId || jobState === 'completed' || jobState === 'failed') return undefined;
    let cancelled = false;
    const startedAt = Date.now();
    const tick = async () => {
      try {
        const token = await getToken();
        const status = await getJobStatus(jobId, token as string);
        if (cancelled) return;
        // A job stuck in `waiting` means no worker picked it up (the server
        // 503s this up-front, but a worker can also die mid-flight) — stop
        // the infinite spinner with an actionable message.
        if (status?.state === 'waiting' && Date.now() - startedAt > 30000) {
          setError(offlineMessage);
          setJobState('failed');
          return;
        }
        if (status?.progress?.stage) setStage(status.progress.stage);
        if (status?.state === 'active' && jobState === 'waiting') setJobState('active');
        if (status?.state === 'completed') {
          setVariants(status.result?.variants || []);
          setJobState('completed');
        } else if (status?.state === 'failed') {
          setError(status.error || 'Generation failed');
          setJobState('failed');
        }
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.message || 'Polling error');
        setJobState('failed');
      }
    };
    tick();
    const interval = setInterval(tick, 1500);
    return () => { cancelled = true; clearInterval(interval); };
  }, [jobId, jobState, getJobStatus, getToken]);

  return { jobState, stage, variants, error, start, fail, reset };
}
